import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import './AnnotatorDashboard.css';

function AnnotatorDashboard() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);

  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Fetch assigned jobs
  const fetchJobs = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/corrections/annotator/jobs', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
        setError('');
      } else if (response.status === 403) {
        setError('You are not authorized as an annotator. Please contact an administrator.');
      } else {
        setError('Failed to fetch jobs');
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to fetch jobs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchJobs();
    }
  }, [isAuthenticated]);

  // Update job status
  const updateJobStatus = async (jobId, status) => {
    try {
      const response = await fetch(`/api/corrections/annotator/job/${jobId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        fetchJobs();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      assigned: 'status-assigned',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled'
    };
    return statusClasses[status] || 'status-pending';
  };

  if (authLoading || isLoading) {
    return (
      <div className="annotator-dashboard">
        <Header />
        <main className="annotator-main">
          <div className="loading">Loading...</div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="annotator-dashboard">
      <Header />
      <main className="annotator-main">
        <div className="annotator-container">
          <h1>Annotator Dashboard</h1>
          <p className="dashboard-subtitle">Your assigned correction jobs</p>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="dashboard-actions">
            <button className="refresh-btn" onClick={fetchJobs}>
              Refresh Jobs
            </button>
          </div>

          {jobs.length === 0 ? (
            <div className="empty-state">
              <p>No jobs assigned yet</p>
              <p className="empty-hint">Check back later for new assignments</p>
            </div>
          ) : (
            <div className="jobs-grid">
              {jobs.map(job => (
                <div key={job.id} className="job-card">
                  <div className="job-header">
                    <h3>Job #{job.id}</h3>
                    <span className={`status-badge ${getStatusBadge(job.status)}`}>
                      {job.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="job-details">
                    <div className="detail-row">
                      <span className="detail-label">User:</span>
                      <span className="detail-value">{job.user_email || 'Unknown'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Duration:</span>
                      <span className="detail-value">{formatDuration(job.audio_duration_seconds)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Payment:</span>
                      <span className="detail-value">${job.cost_usd}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Assigned:</span>
                      <span className="detail-value">{formatDate(job.assigned_at)}</span>
                    </div>
                  </div>

                  <div className="job-actions">
                    {job.status === 'assigned' && (
                      <button
                        className="btn-primary"
                        onClick={() => updateJobStatus(job.id, 'in_progress')}
                      >
                        Start Working
                      </button>
                    )}
                    {job.status === 'in_progress' && (
                      <button
                        className="btn-primary"
                        onClick={() => navigate(`/annotator/job/${job.id}`)}
                      >
                        Continue Editing
                      </button>
                    )}
                    {job.status === 'completed' && (
                      <div className="completed-badge">
                        Completed
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Job Detail Modal */}
          {selectedJob && (
            <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
              <div className="job-detail-modal" onClick={e => e.stopPropagation()}>
                <h3>Job Details - #{selectedJob.id}</h3>

                <div className="modal-details">
                  <p><strong>Status:</strong> {selectedJob.status}</p>
                  <p><strong>User:</strong> {selectedJob.user_email}</p>
                  <p><strong>Duration:</strong> {formatDuration(selectedJob.audio_duration_seconds)}</p>
                  <p><strong>Payment:</strong> ${selectedJob.cost_usd}</p>
                  <p><strong>Assigned:</strong> {formatDate(selectedJob.assigned_at)}</p>
                  {selectedJob.completed_at && (
                    <p><strong>Completed:</strong> {formatDate(selectedJob.completed_at)}</p>
                  )}
                  {selectedJob.notes && (
                    <p><strong>Notes:</strong> {selectedJob.notes}</p>
                  )}
                </div>

                <button className="close-btn" onClick={() => setSelectedJob(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AnnotatorDashboard;
